import type { Match, TournamentFormat } from '@/data/types';
import { computeStandings } from './standings';

/** Большое число для сортировки участников, у которых место ещё не определено. */
const IN_PLAY_RANK = 1_000_000;

export interface PlaceRow {
  /** "1", "2", "3-4", "5-8" или "—" если место ещё не определено. */
  place: string;
  /** Числовой ключ сортировки. Для «в игре» — IN_PLAY_RANK. */
  rank: number;
  participantId: string;
  /** Человекочитаемый статус: "Чемпион", "Финалист", "Выбыл в 1/4", "В игре"… */
  status: string;
}

type GroupsArg = { id: string; participantIds: string[] }[] | undefined;

/** Является ли матч walkover/bye — у такого «проигравшего» нет. */
function isWalkover(m: Match): boolean {
  return m.score?.summary === 'walkover';
}

/** Проигравший COMPLETED-матча (id) или null. */
function loserOf(m: Match): string | null {
  if (m.status !== 'COMPLETED' || !m.score || !m.playerAId || !m.playerBId) return null;
  if (isWalkover(m)) return null;
  const { winnerId } = m.score;
  if (winnerId === m.playerAId) return m.playerBId;
  if (winnerId === m.playerBId) return m.playerAId;
  return null;
}

function winnerOf(m: Match): string | null {
  if (m.status !== 'COMPLETED' || !m.score) return null;
  const { winnerId } = m.score;
  if (winnerId === m.playerAId || winnerId === m.playerBId) return winnerId;
  return null;
}

/** Метка диапазона мест: [lo, hi] → "lo" или "lo-hi". */
function rangeLabel(lo: number, hi: number): string {
  return lo === hi ? String(lo) : `${lo}-${hi}`;
}

function sortRows(rows: PlaceRow[]): PlaceRow[] {
  return rows
    .slice()
    .sort((a, b) => a.rank - b.rank || a.participantId.localeCompare(b.participantId));
}

/**
 * Места для RR / SWISS — позиции в computeStandings (сквозные 1..N).
 */
function placesFromStandings(matches: Match[], participantIds: string[]): PlaceRow[] {
  const standings = computeStandings(matches, participantIds);
  return standings.map((row, idx) => ({
    place: String(idx + 1),
    rank: idx + 1,
    participantId: row.participantId,
    status: idx === 0 ? 'Лидер' : `${idx + 1}-е место`,
  }));
}

/** Метка раунда вылета для SINGLE_ELIM. */
function elimStatusLabel(roundsFromEnd: number): string {
  // roundsFromEnd: 0 = финал, 1 = полуфинал, 2 = четвертьфинал…
  if (roundsFromEnd === 0) return 'Финалист';
  if (roundsFromEnd === 1) return 'Выбыл в полуфинале';
  if (roundsFromEnd === 2) return 'Выбыл в 1/4 финала';
  const denom = 2 ** roundsFromEnd;
  return `Выбыл в 1/${denom} финала`;
}

/**
 * Места для SINGLE_ELIM по плей-офф матчам (одна сетка).
 * `seMatches` — матчи единственной сетки на выбывание.
 * `rankBase` — с какого rank/place начинать (для GROUPS_PLAYOFF — 1).
 * `playerCount` — число реальных участников сетки: верхняя граница диапазона
 *   мест клампится по нему (при не-степени-двойки в сетке есть byes, и
 *   формула 2^N дала бы несуществующие места).
 */
function placesFromSingleElim(
  seMatches: Match[],
  rankBase: number,
  playerCount: number
): PlaceRow[] {
  if (seMatches.length === 0) return [];
  const rounds = Math.max(...seMatches.map((m) => m.round));
  const rows: PlaceRow[] = [];

  // Финал.
  const final = seMatches.find((m) => m.round === rounds && m.status === 'COMPLETED');
  if (final) {
    const champ = winnerOf(final);
    const runnerUp = loserOf(final);
    if (champ) {
      rows.push({
        place: String(rankBase),
        rank: rankBase,
        participantId: champ,
        status: 'Чемпион',
      });
    }
    if (runnerUp) {
      rows.push({
        place: String(rankBase + 1),
        rank: rankBase + 1,
        participantId: runnerUp,
        status: 'Финалист',
      });
    }
  }

  // Проигравшие матчей раунда R (R < rounds): диапазон [2^(rounds-R)+1 .. 2^(rounds-R+1)].
  // Верхняя граница клампится по числу участников — иначе при byes появляются
  // несуществующие места (напр. «7-8» в сетке из 6 игроков).
  for (let r = rounds - 1; r >= 1; r--) {
    const exp = rounds - r;
    const lo = 2 ** exp + 1;
    const hi = Math.min(2 ** (exp + 1), playerCount);
    if (lo > playerCount) continue;
    const loserLabel = elimStatusLabel(exp);
    for (const m of seMatches.filter((mm) => mm.round === r && mm.status === 'COMPLETED')) {
      const loser = loserOf(m);
      if (!loser) continue;
      rows.push({
        place: rangeLabel(rankBase - 1 + lo, rankBase - 1 + hi),
        // rank внутри диапазона одинаков — сортируется по нижней границе.
        rank: rankBase - 1 + lo,
        participantId: loser,
        status: loserLabel,
      });
    }
  }

  return rows;
}

/**
 * SINGLE_ELIM целиком: matches — все матчи сетки, participantIds — все участники.
 */
function placesSingleElim(matches: Match[], participantIds: string[]): PlaceRow[] {
  if (matches.length === 0) return participantIds.map(inPlayRow);
  const placed = placesFromSingleElim(matches, 1, participantIds.length);
  const placedIds = new Set(placed.map((r) => r.participantId));
  const rest = participantIds
    .filter((id) => !placedIds.has(id))
    .map(inPlayRow);
  return sortRows([...placed, ...rest]);
}

function inPlayRow(participantId: string): PlaceRow {
  return { place: '—', rank: IN_PLAY_RANK, participantId, status: 'В игре' };
}

/**
 * DOUBLE_ELIM. Победитель GF → 1, проигравший GF → 2.
 *
 * Места в нижней сетке считаются от ПОЛНОЙ структуры, а не от сыгранных
 * матчей: проигравший последнего раунда нижней сетки (lower-final) → 3,
 * предпоследнего → следующий блок и т.д. Чем раньше раунд вылета — тем
 * ниже (больше) место. Места ещё не сыгранных раундов резервируются,
 * чтобы первый вылетевший не получил ошибочно 3-е место.
 */
function placesDoubleElim(matches: Match[], participantIds: string[]): PlaceRow[] {
  if (matches.length === 0) return participantIds.map(inPlayRow);
  const rows: PlaceRow[] = [];
  const placedIds = new Set<string>();

  // Grand Final — единственный матч stage 'MAIN' с максимальным round
  // (upper-bracket-матчи тоже 'MAIN', но GF идёт раундом позже всех).
  const mainMatches = matches.filter((m) => m.stage === 'MAIN');
  const gfRound = mainMatches.length > 0 ? Math.max(...mainMatches.map((m) => m.round)) : 0;
  const gf = mainMatches.find((m) => m.round === gfRound && m.status === 'COMPLETED');
  if (gf) {
    const champ = winnerOf(gf);
    const runnerUp = loserOf(gf);
    if (champ) {
      rows.push({ place: '1', rank: 1, participantId: champ, status: 'Чемпион' });
      placedIds.add(champ);
    }
    if (runnerUp) {
      rows.push({ place: '2', rank: 2, participantId: runnerUp, status: 'Финалист' });
      placedIds.add(runnerUp);
    }
  }

  // Все LOWER-матчи (не только сыгранные) — нужны, чтобы знать полную
  // структуру нижней сетки и зарезервировать места под каждый раунд.
  const lowerMatches = matches.filter((m) => m.stage === 'LOWER');
  if (lowerMatches.length > 0) {
    const maxLowerRound = Math.max(...lowerMatches.map((m) => m.round));

    // Идём от последнего раунда нижней сетки к первому: lower-final
    // отдаёт место 3, дальше — последовательные блоки.
    let nextPlace = 3;
    for (let round = maxLowerRound; round >= 1; round--) {
      const roundMatches = lowerMatches.filter((m) => m.round === round);
      if (roundMatches.length === 0) continue;
      // Размер блока мест = число матчей раунда (= число проигравших).
      const lo = nextPlace;
      const hi = nextPlace + roundMatches.length - 1;
      const label = rangeLabel(lo, hi);
      for (const m of roundMatches) {
        if (m.status !== 'COMPLETED') continue;
        const loser = loserOf(m);
        if (!loser || placedIds.has(loser)) continue;
        rows.push({
          place: label,
          rank: lo,
          participantId: loser,
          status: 'Выбыл в нижней сетке',
        });
        placedIds.add(loser);
      }
      // Сдвигаем nextPlace на весь блок, даже если раунд ещё не доигран —
      // места под него зарезервированы.
      nextPlace = hi + 1;
    }
  }

  const rest = participantIds.filter((id) => !placedIds.has(id)).map(inPlayRow);
  return sortRows([...rows, ...rest]);
}

/**
 * GROUPS_PLAYOFF. Плей-офф участники — места как SINGLE_ELIM по PLAYOFF-матчам.
 * Не прошедшие группу — ниже всех плей-офф участников, упорядочены по позиции
 * в своей группе.
 */
function placesGroupsPlayoff(
  matches: Match[],
  participantIds: string[],
  groups: GroupsArg
): PlaceRow[] {
  if (matches.length === 0) return participantIds.map(inPlayRow);
  const playoffMatches = matches.filter((m) => m.stage === 'PLAYOFF');
  const groupMatches = matches.filter((m) => m.stage === 'GROUP');

  const rows: PlaceRow[] = [];
  const placedIds = new Set<string>();

  // Участники плей-офф.
  const playoffParticipantIds = new Set<string>();
  for (const m of playoffMatches) {
    if (m.playerAId) playoffParticipantIds.add(m.playerAId);
    if (m.playerBId) playoffParticipantIds.add(m.playerBId);
  }

  if (playoffMatches.length > 0) {
    const playoffPlaces = placesFromSingleElim(playoffMatches, 1, playoffParticipantIds.size);
    for (const r of playoffPlaces) {
      rows.push(r);
      placedIds.add(r.participantId);
    }
    // Участники плей-офф, ещё не получившие место — «в игре».
    for (const id of playoffParticipantIds) {
      if (!placedIds.has(id)) {
        rows.push(inPlayRow(id));
        placedIds.add(id);
      }
    }
  }

  // Не прошедшие группу: ниже всех плей-офф участников.
  const groupOnlyIds = participantIds.filter(
    (id) => !playoffParticipantIds.has(id)
  );
  // rank-база — больше любого плей-офф ранга (но меньше IN_PLAY).
  const base = Math.max(
    playoffParticipantIds.size + 1,
    ...rows.filter((r) => r.rank !== IN_PLAY_RANK).map((r) => r.rank + 1)
  );

  if (groupMatches.length > 0 && groupOnlyIds.length > 0) {
    // Упорядочиваем не прошедших по позиции в своей группе.
    const ordered: string[] = [];
    const groupList = groups ?? [];
    for (const g of groupList) {
      const inThisGroup = g.participantIds.filter((id) => groupOnlyIds.includes(id));
      if (inThisGroup.length === 0) continue;
      const gMatches = groupMatches.filter((m) => m.groupId === g.id);
      const standing = computeStandings(gMatches, g.participantIds);
      for (const row of standing) {
        if (inThisGroup.includes(row.participantId)) ordered.push(row.participantId);
      }
    }
    // Те, кто не попал ни в одну группу (на всякий случай).
    for (const id of groupOnlyIds) {
      if (!ordered.includes(id)) ordered.push(id);
    }
    ordered.forEach((id, idx) => {
      rows.push({
        place: String(base + idx),
        rank: base + idx,
        participantId: id,
        status: 'Не вышел из группы',
      });
      placedIds.add(id);
    });
  } else {
    for (const id of groupOnlyIds) {
      if (!placedIds.has(id)) {
        rows.push(inPlayRow(id));
        placedIds.add(id);
      }
    }
  }

  // Любые оставшиеся участники.
  const rest = participantIds.filter((id) => !placedIds.has(id)).map(inPlayRow);
  return sortRows([...rows, ...rest]);
}

/**
 * Распределение участников по итоговым местам. Pure-функция.
 *
 * @param matches      все матчи турнира
 * @param participantIds  id всех участников
 * @param format       формат турнира
 * @param groups       группы (только для GROUPS_PLAYOFF)
 */
/**
 * Карта `participantId → метка места` из результата `computePlaces`.
 * Включает только участников с определённым местом (place !== «—»).
 */
export function placeMap(rows: PlaceRow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (row.place !== '—') map.set(row.participantId, row.place);
  }
  return map;
}

export function computePlaces(
  matches: Match[],
  participantIds: string[],
  format: TournamentFormat,
  groups?: GroupsArg
): PlaceRow[] {
  switch (format) {
    case 'ROUND_ROBIN':
    case 'SWISS':
      return placesFromStandings(matches, participantIds);
    case 'SINGLE_ELIM':
      return placesSingleElim(matches, participantIds);
    case 'DOUBLE_ELIM':
      return placesDoubleElim(matches, participantIds);
    case 'GROUPS_PLAYOFF':
      return placesGroupsPlayoff(matches, participantIds, groups);
    default:
      return participantIds.map(inPlayRow);
  }
}
